import type { ComponentProps, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cx as cn } from './cx';

const BUTTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-button font-semibold transition duration-200 ease-out active:scale-98 motion-reduce:transform-none';

/** Sizes are separate from variants: classes are joined, not merged, so a
 *  caller overriding `h-12` with `h-10` would leave both and let CSS order pick. */
const BUTTON_SIZES = {
  md: 'h-12 px-6 text-ui-lg',
  sm: 'h-10 px-4 text-ui-strong',
} as const;

export const BUTTON_STYLES = {
  // `brand-strong`, not `brand`: white on the lighter coral fails AA for text.
  primary: `${BUTTON_BASE} bg-brand-strong text-white hover:bg-brand-strong-hover`,
  secondary: `${BUTTON_BASE} border border-hairline-alt bg-surface text-text-primary hover:bg-surface-hover`,
  /** On the dark CTA band. */
  inverse: `${BUTTON_BASE} bg-surface text-ink hover:bg-warm`,
} as const;

type Variant = keyof typeof BUTTON_STYLES;
type Size = keyof typeof BUTTON_SIZES;

export function buttonClass(variant: Variant = 'primary', size: Size = 'md') {
  return `${BUTTON_STYLES[variant]} ${BUTTON_SIZES[size]}`;
}

/** A link styled as a button. In-app routes and hash links use the router;
 *  anything absolute (mailto:, https:) a plain anchor. */
export function ButtonLink({
  children,
  className,
  size = 'md',
  to,
  variant = 'primary',
  ...rest
}: {
  children: ReactNode;
  className?: string;
  size?: Size;
  to: string;
  variant?: Variant;
} & Omit<ComponentProps<'a'>, 'href'>) {
  const classes = cn(buttonClass(variant, size), className);
  if (/^(https?:|mailto:|#)/.test(to) || to.endsWith('.pdf')) {
    return (
      <a className={classes} href={to} {...rest}>
        {children}
      </a>
    );
  }
  return (
    <Link className={classes} to={to} {...rest}>
      {children}
    </Link>
  );
}

export function Container({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mx-auto w-full max-w-6xl px-6 md:px-12', className)}>
      {children}
    </div>
  );
}

/** The small uppercase label design.md uses above titles. */
export function Eyebrow({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cn(
        'text-small-label uppercase text-text-secondary',
        className,
      )}
    >
      {children}
    </p>
  );
}

export function SectionHeading({
  eyebrow,
  id,
  intro,
  title,
}: {
  eyebrow: string;
  id: string;
  intro?: ReactNode;
  title: ReactNode;
}) {
  return (
    <div className="max-w-3xl">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2
        className="mt-3 text-balance font-report text-report-section font-medium text-text-primary"
        id={id}
      >
        {title}
      </h2>
      {intro ? (
        <p className="mt-4 text-report-lede text-text-secondary">{intro}</p>
      ) : null}
    </div>
  );
}

export function Card({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'rounded-card border border-hairline bg-surface p-6 shadow-soft',
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

/** A tiny pill: soft fill, dark text (design.md > Tags). */
export function Tag({
  children,
  className,
  tone = 'neutral',
}: {
  children: ReactNode;
  className?: string;
  tone?: 'neutral' | 'critical' | 'major' | 'success';
}) {
  const fill = {
    critical: 'bg-tag-critical',
    major: 'bg-tag-major',
    neutral: 'bg-tag-minor',
    success: 'bg-tag-success',
  }[tone];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-pill px-2.5 py-0.5 text-caption font-medium text-text-primary',
        fill,
        className,
      )}
    >
      {children}
    </span>
  );
}

export function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
